import React, {useEffect, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {MaskDefault} from "../../../../utils/mask.js";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaFilter, FaPlus, FaPrint, FaSearch} from "react-icons/fa";
import individualService from "../../../../services/web/individual-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import IndividualGrid from "./components/individual-grid.jsx";
import IndividualCards from "./components/individual-cards.jsx";
import {getIndividualDefaultPageSize} from "./components/individual-grid-pagination.jsx";
import IndividualFilter from "./components/individual-filter/individual-filter.jsx";
import INDIVIDUAL_FILTER_DEFINITIONS from "./resources/filter-definitions.js";
import INITIAL_FILTER from "./resources/initial-filter.js";
import HeaderTemplateButton from "../../../../components/button/header-template-button.jsx";
import PrintTemplate from "../../../../components/reports/print-reports/print-template.jsx";
import PrintFilters from "./components/individual-filter/print-filters.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {countAppliedFilters, hasFilterValue} from "./resources/filter-utils.js";
import {useSearchParams} from "react-router";

const VISUALIZATION_OPTIONS = [
    {value: "grid", label: "Tabela"},
    {value: "cards", label: "Cards"},
]

const IndividualList = () => {
    const navigate = navigateHook();
    const [searchParams] = useSearchParams();
    const printComponentRef = useRef(null);
    const hasSearchedUrlName = useRef(false);
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [isLoading, setIsLoading] = useState(false)
    const [filter, setFilter] = useState(() => {
        const name = searchParams.get("name")

        return name === null ? INITIAL_FILTER : {...INITIAL_FILTER, name}
    })
    const [hasFilter, setHasFilter] = useState(false)
    const [data, setData] = useState([])
    const [modeVisualization, setModeVisualization] = useState("cards")
    const [selectedModeVisualization, setSelectedModeVisualization] = useState("cards")
    const [openFilter, setOpenFilter] = useState(false)
    const [imageMode, setImageMode] = useState(filter.imageMode)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(() => getIndividualDefaultPageSize(selectedModeVisualization))
    const [hasSearched, setHasSearched] = useState(false)
    const [isPrinting, setIsPrinting] = useState(false)
    const [isPreparingPrint, setIsPreparingPrint] = useState(false)

    const onChange = ({target: {id, value}}) => setFilter(prev => ({...prev, [id]: value}));
    const onChangeCustom = (value, id) => setFilter(prev => ({...prev, [id]: value}));

    const handleFiltersApplied = () => {
        setHasFilter(countAppliedFilters(filter) > 0)
    }

    const clear = () => {
        setHasFilter(false)
        setFilter(INITIAL_FILTER)
        setHasSearched(false)
        setData([])
    }

    const search = () => {
        const defaultPageSize = getIndividualDefaultPageSize(selectedModeVisualization)

        setIsLoading(true)
        const filterPrepared = prepareFilters()

        handleFiltersApplied()

        const data = {
            filters: filterPrepared,
            perPage: defaultPageSize,
            page: 1,
            photoFace: filter.photoFace,
            phoneticSearch: filter.phoneticSearch,
        }

        individualService.list(data)
            .then(({data}) => {
                setCurrentPage(1)
                setPageSize(defaultPageSize)
                setData(data)
                setImageMode(filter.imageMode)
                setModeVisualization(selectedModeVisualization)
                setHasSearched(true)
            })
            .catch((error) => {
                console.error(error);
                projectUtils.swalErrorMessage("", "Não foi possível prosseguir.", "Ok");
            })
            .finally(() => setIsLoading(false))
    }

    useEffect(() => {
        if (!searchParams.has("name") || hasSearchedUrlName.current) return

        hasSearchedUrlName.current = true
        search()
    }, [searchParams])

    const handleValue = (filter, field) => {
        if (field === "dtBirth_max" || field === "dtBirth_min") {
            const value = filter[field]

            if (!hasFilterValue(value)) return value

            const age = Number(value)
            if (!Number.isFinite(age)) return value

            const year = new Date().getFullYear() - age

            return `${year}-01-01`
        }

        if (field === "associations") {
            return filter[field].map(association => association.value).join(", ")
        }

        return filter[field]
    }

    const prepareFilters = () => {
        return INDIVIDUAL_FILTER_DEFINITIONS.reduce((filters, {field, ...filterDefault}) => {
            let valueLocal = null, valueType = null;
            if (field === "description") {
                valueLocal = filter.local
                valueType = filter.type
            }

            const value = handleValue(filter, field)

            if (!hasFilterValue(value || valueLocal || valueType)) return filters

            filters.push({
                ...filterDefault,
                valueLocal,
                valueType,
                value: filterDefault.name === "cpf" ? value.replace(/\D/g, "") : value,
            })

            return filters
        }, [])
    }

    const openForm = () => {
        const urlDev = `/individuals/form`;
        navigate(urlDev)
    }

    const handlePrintingChange = (printing) => {
        setIsPrinting(printing)
        setIsPreparingPrint(false)
    }

    const printIndividuals = () => {
        setIsPreparingPrint(true)

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!printComponentRef.current) {
                    setIsPreparingPrint(false)
                    return
                }

                printComponentRef.current.print()
            })
        })
    }

    useGlobalKey("Enter", () => {
        if (openFilter) setOpenFilter(false)
        search()
    });

    useGlobalKey("Escape", () => {
        if (!openFilter) clear()
    });

    return (
        <>
            <Template isLoading={isLoading || isPreparingPrint || isPrinting}>
                <Template.Header>
                    <Template.Title>
                        Indivíduos
                    </Template.Title>

                    <div className={"flex gap-2"}>
                        <Button onClick={openForm}>
                            <FaPlus/><span className="font-bold">Adicionar indivíduo</span>
                        </Button>
                        <Button title="Imprimir" onClick={printIndividuals}>
                            <FaPrint/>
                        </Button>

                        <HeaderTemplateButton selected={headerSelected} setSelected={setHeaderSelected} />
                    </div>
                </Template.Header>
                <Template.Body>
                    <Card>
                        <Card.Body>
                            <SentryRow>
                                <div className="col-span-5 flex gap-4">
                                    <InputForm
                                        classNameWrapper={"w-full"}
                                        inputClass={"w-full"}
                                        id="name"
                                        name="name"
                                        value={filter.name}
                                        onChange={onChange}
                                        label="Nome"/>

                                    <InputForm
                                        id="cpf"
                                        label="CPF"
                                        value={filter.cpf}
                                        onChange={onChange}
                                        onBlur={onChange}
                                        mask={MaskDefault.cpf}
                                    />
                                </div>

                                <div className={"col-span-4 flex items-center gap-3"}>
                                    <div>
                                        <InputCheckbox
                                            id="imageMode"
                                            label="Exibir imagens de anexos"
                                            checked={filter.imageMode}
                                            onChange={({target: {checked}}) => onChangeCustom(checked, "imageMode")}
                                        />
                                    </div>
                                    <div>
                                        <InputCheckbox
                                            id="photoFace"
                                            label="Somente com foto de face"
                                            checked={filter.photoFace}
                                            onChange={({target: {checked}}) => onChangeCustom(checked, "photoFace")}
                                        />
                                    </div>
                                    <div>
                                        <InputCheckbox
                                            id="phoneticSearch"
                                            label="Busca fonética"
                                            checked={filter.phoneticSearch}
                                            onChange={({target: {checked}}) => onChangeCustom(checked, "phoneticSearch")}
                                        />
                                    </div>
                                </div>

                                <div className={"col-span-3 flex gap-4"}>
                                    <SelectComponent
                                        classNameWrapper="w-full"
                                        instanceId="modeVisualization"
                                        label="Visualização"
                                        options={VISUALIZATION_OPTIONS}
                                        value={selectedModeVisualization}
                                        onChange={(option) => setSelectedModeVisualization(option.value)}
                                        isClearable={false}
                                    />
                                    <div className="flex gap-2 justify-end items-center">
                                        <Button target={"primary-outline"} className={`${hasFilter ? "border-[#ffa500]!" : ""}`} onClick={() => setOpenFilter(true)}>
                                            {hasFilter && <FaFilter color={"#ffa500"}/>}
                                            {!hasFilter && <FaFilter />}
                                        </Button>
                                        <Button target="primary-outline" onClick={clear} title="Limpar">
                                            <FaEraser/>
                                        </Button>
                                        <Button target="primary" onClick={search} title="Buscar">
                                            <FaSearch/>
                                        </Button>
                                    </div>
                                </div>
                            </SentryRow>
                        </Card.Body>
                    </Card>

                    <Card className={"mt-2"}>
                        <Card.Body className={"relative"}>
                            {modeVisualization === "cards" && (
                                <IndividualCards
                                    rowData={data}
                                    imageMode={imageMode}
                                    hasSearched={hasSearched}
                                    currentPage={currentPage}
                                    pageSize={pageSize}
                                    onCurrentPageChange={setCurrentPage}
                                    onPageSizeChange={setPageSize}
                                />
                            )}
                            {modeVisualization === "grid" && (
                                <IndividualGrid
                                    rowData={data}
                                    currentPage={currentPage}
                                    pageSize={pageSize}
                                    onCurrentPageChange={setCurrentPage}
                                    onPageSizeChange={setPageSize}
                                />
                            )}
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <IndividualFilter
                open={openFilter}
                setOpen={setOpenFilter}
                filter={filter}
                setFilter={setFilter}
                onSearch={search}
            />

            <PrintTemplate
                entity={headerSelected}
                title={"Indivíduos"}
                ref={printComponentRef}
                setIsPrinting={handlePrintingChange}
                printClassName={"hidden print:block"}
            >
                {isPrinting && (
                    <>
                        <div id="filters-print">
                            <PrintFilters filter={filter}/>
                        </div>
                        <div className={"w-full"}>
                            <IndividualCards rowData={data} printMode={true}/>
                        </div>
                    </>
                )}
            </PrintTemplate>
        </>
    );
};

export default IndividualList;
